// Sorting for the static library, shared by the card grid and the table.
//
// Both views sort the same list with the same comparators and the same state,
// so switching views re-renders the ads you were already looking at in the same
// order. Previously the table owned its own sort and the grid had none, which
// meant the two views disagreed about what "first" meant.

import { StaticAdSummary } from './types';

export type StaticSortField =
  | 'overallGrade'
  | 'startDate'
  | 'scrapedAt'
  | 'advertiser'
  | 'keyword'
  | 'pipelineStatus'
  | 'staticFormat'
  | 'visualHook'
  | 'layout'
  | 'textDensity'
  | 'aspectRatio';

/** The options offered in the grid's sort menu, in the order they're shown. */
export const STATIC_SORT_OPTIONS: Array<{ field: StaticSortField; label: string }> = [
  { field: 'overallGrade', label: 'Rank' },
  { field: 'startDate', label: 'Started running' },
  { field: 'scrapedAt', label: 'Date fetched' },
  { field: 'advertiser', label: 'Advertiser' },
  { field: 'keyword', label: 'Keyword' },
  { field: 'pipelineStatus', label: 'Analysis status' },
  { field: 'staticFormat', label: 'Format' },
  { field: 'visualHook', label: 'Hook' },
  { field: 'layout', label: 'Layout' },
  { field: 'textDensity', label: 'Text density' },
  { field: 'aspectRatio', label: 'Aspect ratio' },
];

/** Every sortable key, for validating persisted/foreign sort state. */
export const STATIC_SORT_FIELDS: StaticSortField[] = STATIC_SORT_OPTIONS.map(opt => opt.field);

/**
 * Missing values sort last in BOTH directions.
 *
 * Ads are ranked only once analyzed, so an un-analyzed ad has no grade — and
 * with plain null handling, flipping to ascending would fill the top of the
 * grid with ungraded ads. "Worst first" should mean the worst ad that has a
 * score, not the ads that don't have one.
 */
function nullsLast(cmp: number, aMissing: boolean, bMissing: boolean, dir: 'asc' | 'desc'): number {
  if (aMissing && bMissing) return 0;
  // Pre-negated, because the caller flips the sign for descending.
  if (aMissing) return dir === 'asc' ? 1 : -1;
  if (bMissing) return dir === 'asc' ? -1 : 1;
  return cmp;
}

function timeOf(value?: string | null): number {
  if (!value) return NaN;
  const ms = new Date(value.includes('T') ? value : value.replace(' ', 'T')).getTime();
  return Number.isNaN(ms) ? NaN : ms;
}

function advertiserOf(ad: StaticAdSummary): string {
  return (ad.advertiserName || ad.pageName || '').trim();
}

// Rough "how far along" order, so sorting by status groups the library into
// things needing attention rather than alphabetising the status word.
const STATUS_RANK: Record<string, number> = {
  FAILED: 0, NEW: 1, PENDING: 2, RUNNING: 3, DONE: 4,
};

export function staticComparators(dir: 'asc' | 'desc'): Record<string, (a: StaticAdSummary, b: StaticAdSummary) => number> {
  const byText = (value: (ad: StaticAdSummary) => string | null | undefined) =>
    (a: StaticAdSummary, b: StaticAdSummary) => {
      const av = (value(a) || '').trim();
      const bv = (value(b) || '').trim();
      return nullsLast(av.localeCompare(bv, undefined, { sensitivity: 'base' }), !av, !bv, dir);
    };
  return {
    overallGrade: (a, b) => nullsLast(
      (a.overallGrade ?? 0) - (b.overallGrade ?? 0),
      a.overallGrade == null, b.overallGrade == null, dir,
    ),
    startDate: (a, b) => {
      const at = timeOf(a.startDate);
      const bt = timeOf(b.startDate);
      return nullsLast(at - bt, Number.isNaN(at), Number.isNaN(bt), dir);
    },
    scrapedAt: (a, b) => {
      const at = timeOf(a.scrapedAt);
      const bt = timeOf(b.scrapedAt);
      return nullsLast(at - bt, Number.isNaN(at), Number.isNaN(bt), dir);
    },
    advertiser: (a, b) => {
      const an = advertiserOf(a);
      const bn = advertiserOf(b);
      return nullsLast(an.localeCompare(bn, undefined, { sensitivity: 'base' }), !an, !bn, dir);
    },
    keyword: (a, b) => {
      const ak = a.keyword || '';
      const bk = b.keyword || '';
      return nullsLast(ak.localeCompare(bk, undefined, { sensitivity: 'base' }), !ak, !bk, dir);
    },
    pipelineStatus: (a, b) =>
      (STATUS_RANK[a.pipelineStatus] ?? 99) - (STATUS_RANK[b.pipelineStatus] ?? 99),
    staticFormat: byText(ad => ad.staticFormat),
    visualHook: byText(ad => ad.visualHook),
    layout: byText(ad => ad.layout),
    textDensity: byText(ad => ad.textDensity),
    aspectRatio: byText(ad => ad.aspectRatio),
  };
}

/** Persisted or foreign sort keys pass through here: anything without a
 * comparator falls back to the default so a stale localStorage value can't
 * leave the library silently unsorted. */
export function sanitizeStaticSortField(field: string | null | undefined): StaticSortField {
  return STATIC_SORT_FIELDS.includes(field as StaticSortField) ? (field as StaticSortField) : 'scrapedAt';
}

/** How long an ad has been running, as a compact label for a card. */
export function runningSinceLabel(startDate?: string | null): string | null {
  if (!startDate) return null;
  const ms = timeOf(startDate);
  if (Number.isNaN(ms)) return null;
  const days = Math.floor((Date.now() - ms) / 86_400_000);
  if (days < 0) return null;
  if (days === 0) return 'started today';
  if (days === 1) return '1 day running';
  if (days < 60) return `${days} days running`;
  return `${Math.floor(days / 30)} months running`;
}
